import { useEffect } from "react";
import useResource from "./useResource";
import { watchFinanceChanges } from "../lib/financeChanges";

export const LIVE_BOOKING_INTERVAL = 10000;

/** Background polling is read-only, scoped to the current loader, and single-flight. */
export default function useLiveResource(loader, initialData, enabled = true, { financeTenant = "" } = {}) {
  const resource = useResource(loader, initialData);
  const { refresh, loading, error } = resource;
  useEffect(() => {
    if (!enabled || loading || error) return undefined;
    let disposed = false, inFlight = false, timer, generation = 0;
    const visible = () => document.visibilityState !== "hidden";
    const schedule = () => {
      clearTimeout(timer);
      if (!disposed && visible()) timer = setTimeout(update, LIVE_BOOKING_INTERVAL);
    };
    const update = async (supersede = false) => {
      if (disposed || (inFlight && !supersede) || !visible()) return;
      clearTimeout(timer); inFlight = true;
      const request = ++generation;
      try { await refresh(supersede ? { supersede: true } : undefined); }
      finally { if (request === generation) { inFlight = false; schedule(); } }
    };
    const focused = () => { if (visible()) update(); };
    const visibility = () => { if (visible()) update(); else clearTimeout(timer); };
    schedule();
    window.addEventListener("focus", focused);
    document.addEventListener("visibilitychange", visibility);
    const stop = watchFinanceChanges(financeTenant, () => update(true));
    return () => {
      disposed = true; clearTimeout(timer);
      window.removeEventListener("focus", focused);
      document.removeEventListener("visibilitychange", visibility);
      stop();
    };
  }, [enabled, loading, error, refresh, financeTenant]);
  return resource;
}
